import type { IButton } from '@fluentui/react/lib/Button';
import * as React from 'react';
import { CalendarHeaderProps, CalendarViewType } from '../schema';
import { getCalendarListContextMenuItems } from 'owa-calendar-list-context-menu-items';
import type { CalendarEntry } from 'owa-graph-schema';
import type { EventsLoadState } from 'owa-calendar-events-loader';
import {
    calendarPickerPlaceholderText,
    calendarPickerMultipleCalendarsText,
} from '../calendar.locstring.json';
import loc from 'owa-localize';
import getDisplayNameForCalendar from '../utils/getDisplayNameForCalendar';
import { useElementSizeTracker } from 'owa-react-hooks/lib/useElementSizeTracker';
import { getCalendarFolderIdForCompose } from 'owa-calendar-forms-utils/lib/utils/getCalendarFolderIdForCompose';
import getDefaultStart from 'owa-calendar-forms-common/lib/utils/getDefaultStart';
import { today } from 'owa-datetime';
import { getFirstFocusable } from '@fluentui/react/lib/Utilities';
import {
    HideNewEventLabel,
    BoardCalendarAssetEntrySource,
    CalendarPickerDefaultMaxWidth,
    AssetContextMenuOffset,
} from '../constants';
import { tryShowQuickCompose } from 'owa-calendar-quick-compose/lib/actions/tryShowQuickCompose';
import EventScope from 'owa-service/lib/contract/EventScope';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { useCalendarAssetCallbacks } from 'owa-timestream-asset-sdk-calendars';
import { getFluentDateRangeType } from '../utils/calendarViewTypeUtils';
import { convertToOwaDate, getFirstDayOfWeek, getWorkDays } from '../utils/dateUtils';
import {
    createDateRangeFromType,
    isDateInDateRange,
    createWorkWeekDaysArray,
} from 'owa-datetime-utils';

export default function useCalendarHeaderViewState(
    {
        isDragPreview,
        isNewlyCreated,
        combinedCalendarIds,
        isLoaded,
        onSelectCalendar,
        scenarioId,
        title,
        onChangeTitle,
        selectedDate,
        numDaysInDayView,
        viewType,
    }: CalendarHeaderProps,
    pickerComponentRef: React.RefObject<IButton | null>
) {
    React.useLayoutEffect(() => {
        if (
            (!combinedCalendarIds || combinedCalendarIds.length === 0) &&
            !isDragPreview &&
            isNewlyCreated
        ) {
            window.setTimeout(() => {
                pickerComponentRef.current?.openMenu(true);
            }, 0);
        }
    }, [combinedCalendarIds, pickerComponentRef.current, isDragPreview, isNewlyCreated]);

    const isCalendarSelected = React.useCallback(
        (calendar: CalendarEntry) => {
            return combinedCalendarIds.includes(calendar.calendarId.id);
        },
        [combinedCalendarIds]
    );

    let pickerLabel: string;
    if (title) {
        pickerLabel = title;
    } else if (combinedCalendarIds.length === 0) {
        pickerLabel = loc(calendarPickerPlaceholderText);
    } else if (combinedCalendarIds.length === 1) {
        pickerLabel = getDisplayNameForCalendar(combinedCalendarIds[0]);
    } else {
        pickerLabel = loc(calendarPickerMultipleCalendarsText, combinedCalendarIds.length);
    }

    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const calendarPickerRef = React.useRef<HTMLDivElement | null>(null);
    const newEventButtonRef = React.useRef<HTMLDivElement | null>(null);
    const [containerRect] = useElementSizeTracker(containerRef);
    const [pickerButtonRect] = useElementSizeTracker(calendarPickerRef);
    const [newEventButtonRect] = useElementSizeTracker(newEventButtonRef);

    const shouldShowNewEventText =
        containerRect && pickerButtonRect ? containerRect?.width > HideNewEventLabel : true;

    const shouldShowNewButton = getCalendarFolderIdForCompose(combinedCalendarIds) != null;

    const composeCalendarFolderId = getCalendarFolderIdForCompose(combinedCalendarIds);

    const calendarPickerMaxWidth =
        containerRect && newEventButtonRect
            ? Math.max(
                  containerRect.width - newEventButtonRect.width - AssetContextMenuOffset,
                  CalendarPickerDefaultMaxWidth
              ) // calendar picker button shouuld never be less than default 100px
            : CalendarPickerDefaultMaxWidth; // if for some reason rect refs are undefined default to 100px width

    const newEventOnClick = React.useCallback(() => {
        const fluentDateRangeType = getFluentDateRangeType(viewType);
        const workDays = getWorkDays();
        const firstDayOfWeek = getFirstDayOfWeek();

        const dateRange = createDateRangeFromType(
            fluentDateRangeType,
            convertToOwaDate(selectedDate),
            firstDayOfWeek,
            createWorkWeekDaysArray(workDays),
            numDaysInDayView
        );

        const startDate =
            viewType === CalendarViewType.Month
                ? convertToOwaDate(selectedDate)
                : isDateInDateRange(dateRange, today())
                ? today()
                : dateRange.start;

        let initialCalendarEvent = {
            Start: getDefaultStart(startDate),
            ParentFolderId: composeCalendarFolderId,
        } as CalendarEvent;

        tryShowQuickCompose(
            initialCalendarEvent,
            BoardCalendarAssetEntrySource,
            EventScope.Default,
            getNewEventButtonAnchorElement(scenarioId)
        );
    }, [composeCalendarFolderId]);

    const newEventButtonAnchorId = getNewEventButtonAnchorId(scenarioId);

    const { onOpenAddCalendar } = useCalendarAssetCallbacks();

    return {
        pickerLabel,
        pickerItems: [
            getCalendarListContextMenuItems(
                true /* shouldShowAllPickerOptions */,
                true /* shouldShowConnectedAccounts */,
                onSelectCalendar,
                canToggle,
                isCalendarSelected,
                getCalendarLoadState
            ),
        ],
        showSpinner: !isLoaded && combinedCalendarIds.length > 0,
        containerRef: containerRef,
        calendarPickerRef: calendarPickerRef,
        shouldShowNewEventText: shouldShowNewEventText,
        shouldShowNewButton: shouldShowNewButton,
        newEventOnClick: newEventOnClick,
        newEventButtonAnchorId: newEventButtonAnchorId,
        onWheel: onWheel,
        title: title,
        onChangeTitle,
        calendarPickerMaxWidth: calendarPickerMaxWidth,
        newEventButtonRef: newEventButtonRef,
        onOpenAddCalendar: onOpenAddCalendar,
    };
}

const canToggle = (calendar: CalendarEntry, isSelected: boolean) => true;

const getCalendarLoadState = (calendar: CalendarEntry): EventsLoadState => {
    // we immediately switch views when a calendar is selected, so for now don't need to track whether items have finished loading or not
    return 'Loaded';
};

function getNewEventButtonAnchorElement(scenarioId: string) {
    const anchorName = getNewEventButtonAnchorId(scenarioId);
    const newEventButtonElem = document.getElementById(anchorName) as HTMLElement;
    return getFirstFocusable(newEventButtonElem, newEventButtonElem, true) as HTMLDivElement;
}

function getNewEventButtonAnchorId(scenarioId: string) {
    return scenarioId + '_NewButton';
}

function onWheel(ev: React.WheelEvent<HTMLDivElement>) {
    // prevent canvas from panning
    ev.stopPropagation();
}
